// IF (03/11/20): This logic is currently duplicated on the client and server which is a recipe for disaster
// but leaving it as-is for now until we figure out to share code between the client and server

import moment from 'moment';
import Mustache from 'mustache';

import { APPLICATION_DOMAIN } from '../../config/config';

type EmployeeMessageTemplateOptions = {
  user: AuthenticatedUser;
  org: Org;
  startDateTriggeredCommunication?: StartDateTriggeredCommunication;
  employee?: Employee | null;
  dynamicReplacements?: { [variableName: string]: string };
};
type EmployeeContext = {
  firstName: string;
  casualName: string;
  lastName: string;
  jobTitle: string | null | undefined;
  referralLink: string;
  companyName: string;
  yearsEmployed: string;
};
type UserContext = {
  firstName: string;
  lastName: string;
  fullName: string;
};

export class EmployeeMessageTemplateParser {
  employee: Employee | null;

  user: AuthenticatedUser;

  org: Org;

  startDateTriggeredCommunication: StartDateTriggeredCommunication | null;

  referenceDate: moment.Moment;

  // Any additional replacement variables to include. Currently used for url replacements as there
  // is a variable number of URL variables (url1, url2, etc)
  dynamicReplacements: { [variableName: string]: string };

  context!: { employee: EmployeeContext; user: UserContext; [variableName: string]: unknown };

  constructor({
    user,
    org,
    startDateTriggeredCommunication,
    employee,
    dynamicReplacements,
  }: EmployeeMessageTemplateOptions) {
    this.user = user;
    this.org = org;
    this.startDateTriggeredCommunication = startDateTriggeredCommunication || null;
    this.employee = employee || null;
    this.dynamicReplacements = dynamicReplacements || {};
    this.referenceDate = moment.utc();
    this.generateContext();
  }

  setReferenceDate(date: moment.Moment) {
    this.referenceDate = date;
    this.generateContext();
  }

  employeeContext() {
    const { employee } = this;

    if (!employee) {
      return {
        firstName: '{{{employee.firstName}}}',
        casualName: '{{{employee.casualName}}}',
        lastName: '{{{employee.lastName}}}',
        jobTitle: '{{{employee.jobTitle}}}',
        referralLink: '{{{employee.referralLink}}}',
        companyName: '{{{employee.companyName}}}',
        yearsEmployed: '{{{employee.yearsEmployed}}}',
      };
    }

    const prefersSpanish = employee.Contact.Person.languagePreference === 'es';
    let yearsEmployed;

    if (!employee.startDate) {
      yearsEmployed = prefersSpanish ? 'algún tiempo' : 'some time';
    } else {
      const {
        sendInterval,
        sendIntervalType,
        isRecurring,
        recurrenceInterval,
        recurrenceIntervalType,
      } = this.startDateTriggeredCommunication || {};
      let referenceDay = this.referenceDate || moment.utc();
      const isAnnualRecurrence =
        isRecurring && recurrenceInterval === 1 && recurrenceIntervalType === 'YEAR';
      const sendIntervalDiffers =
        sendInterval !== recurrenceInterval || sendIntervalType !== recurrenceIntervalType;
      if (isAnnualRecurrence && sendIntervalDiffers && sendInterval && sendIntervalType) {
        const durationAmount = sendInterval * -1;
        const durationType: 'day' | 'week' | 'month' | 'year' =
          sendIntervalType.toLowerCase() as any;
        referenceDay = referenceDay.add(durationAmount, durationType);
      }
      const yearsEmployedNumber = referenceDay.diff(
        moment.utc(employee.startDate).startOf('day'),
        'year',
      );
      if (yearsEmployedNumber === 0) {
        yearsEmployed = prefersSpanish ? 'menos de un año' : 'less than a year';
      } else {
        yearsEmployed = `${yearsEmployedNumber} ${prefersSpanish ? 'año' : 'year'}${
          yearsEmployedNumber === 1 ? '' : 's'
        }`;
      }
    }

    return {
      firstName: employee.Contact.Person.firstName,
      casualName: employee.nickname || employee.Contact.Person.firstName,
      lastName: employee.Contact.Person.lastName,
      jobTitle: employee.jobTitle,
      referralLink: `${APPLICATION_DOMAIN}/refer/${employee.employeeHash}`,
      companyName: this.org.name,
      yearsEmployed,
    };
  }

  userContext() {
    return {
      firstName: this.user.firstName,
      lastName: this.user.lastName,
      fullName: `${this.user.firstName} ${this.user.lastName}`,
    };
  }

  generateContext() {
    this.context = {
      employee: this.employeeContext(),
      user: this.userContext(),
      ...this.dynamicReplacements,
    };
  }

  parse(message: string) {
    return Mustache.render(message, this.context);
  }
}
